import { AfterViewInit, Directive, Input } from "@angular/core";
import { FilterMetadata, FilterOperator } from "primeng/api";
import { Table } from "primeng/table";
import { ObjectUtils } from "primeng/utils";

interface filterTableDateConfig {
  field: string;
  pipe: any;
  transform: string;
}

@Directive({
    selector: '[nexusGlobalFilterWithDate]',
    standalone: false
})

export class GlobalFilterWithDateDirective implements AfterViewInit {
  @Input('nexusGlobalFilterWithDate') filterTableDateConfig: filterTableDateConfig[];
  
  constructor(public table: Table) {}

  ngAfterViewInit(): void {
    this.table._filter = () => {
        this.filter();
    };
  }

  filter() {
    if (!this.table.restoringFilter) {
      this.table.first = 0;
      this.table.firstChange.emit(this.table.first);
    }

    if (this.table.lazy) {
      this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
    } else {
      if (!this.table.value) {
        return;
      }

      if (!this.table.hasFilter()) {
        this.table.filteredValue = null;

        if (this.table.paginator) {
          this.table.totalRecords = this.table.value ? this.table.value.length : 0;
        }
      } else {
        let globalFilterFieldsArray;

        if (this.table.filters['global']) {
          if (!this.table.columns && !this.table.globalFilterFields) {
            throw new Error('Global filtering requires dynamic columns or globalFilterFields to be defined.');
          } else {
            globalFilterFieldsArray = this.table.globalFilterFields || this.table.columns;
          }
        }

        this.table.filteredValue = [];

        for (let item = 0; item < this.table.value.length; item++) {
          let localMatch = true;
          let globalMatch = false;
          let localFiltered = false;

          for (const prop in this.table.filters) {
            // eslint-disable-next-line no-prototype-builtins
            if (this.table.filters.hasOwnProperty(prop) && prop !== 'global') {
              localFiltered = true;
              const filterField = prop;
              const filterMeta = this.table.filters[filterField];

              if (Array.isArray(filterMeta)) {
                for (const meta of filterMeta) {
                  localMatch = this.table.executeLocalFilter(filterField, this.table.value[item], meta);

                  if (
                    (meta.operator === FilterOperator.OR && localMatch)
                    || (meta.operator === FilterOperator.AND && !localMatch)
                  ) {
                    break;
                  }
                }
              } else {
                localMatch = this.table.executeLocalFilter(filterField, this.table.value[item], filterMeta);
              }

              if (!localMatch) {
                break;
              }
            }
          }

          if (this.table.filters['global'] && !globalMatch && globalFilterFieldsArray) {
            for (let element = 0; element < globalFilterFieldsArray.length; element++) {
              const globalFilterField = globalFilterFieldsArray[element].field || globalFilterFieldsArray[element];
              const filterMode = (<FilterMetadata> this.table.filters['global']).matchMode;
              const filter = (<FilterMetadata> this.table.filters['global']).value;
              let value = ObjectUtils.resolveFieldData(this.table.value[item], globalFilterField);

              // Overridden part for date filtering. Start
              this.filterTableDateConfig.forEach(config => {
                if (globalFilterField === config.field) {
                  value = new config.pipe('en-US').transform(value, config.transform);
                  return;
                }
              });
              // End

              globalMatch = this.table.filterService.filters[filterMode](value, filter, this.table.filterLocale);

              if (globalMatch) {
                break;
              }
            }
          }

          let matches: boolean;

          if (this.table.filters['global']) {
            matches = localFiltered ? (localFiltered && localMatch && globalMatch) : globalMatch;
          } else {
            matches = localFiltered && localMatch;
          }

          if (matches) {
            this.table.filteredValue.push(this.table.value[item]);
          }
        }

        if (this.table.filteredValue.length === this.table.value.length) {
          this.table.filteredValue = null;
        }

        if (this.table.paginator) {
          this.table.totalRecords = this.table.filteredValue ? this.table.filteredValue.length : this.table.value ? this.table.value.length : 0;
        }
      }
    }

    this.table.onFilter.emit(this.table.filters);

    this.table.tableService.onValueChange(this.table.value);

    if (this.table.isStateful() && !this.table.restoringFilter) {
      this.table.saveState();
    }

    if (this.table.restoringFilter) {
      this.table.restoringFilter = false;
    }

    this.table.cd.markForCheck();

    if (this.table.scrollable) {
      this.table.resetScrollTop();
    }
  }
}
